import { useState } from 'react';
import { Grid, Card, CardHeader, CardContent, CardActions, Button, Typography, IconButton, useTheme, Tooltip } from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import styled from '@emotion/styled';
import SimplePagination from '../../../utils/utilities/SimplePagination';

import { Link } from 'react-router-dom';

import { CardBlock, CardBlockHeader, CardBlockContainer, CardSpacer } from '../../../utils/utilities/CardComponents';

import useSimpleTable from '../../../hooks/useSimpleTable';

import { usePageData } from '../../../contexts/PageData';
import { BasicFlex, LabelUpper } from '../../../utils/Common';

import { formatGenericDate, formatCurrency } from '../../../utils/util';

import DownloadButton from '../../../utils/utilities/Buttons/DownloadButton';
import AttachEmailRoundedIcon from '@mui/icons-material/AttachEmailRounded';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import useIsMobile from '../../../hooks/useIsMobile';

import DeleteDialog from '../../../utils/utilities/DeleteDialog';

import { WarningMessage } from '../../Invoices/Common';

const MAX_ITEMS_SHOWN = 3;
const CARD_SPACING = 2; // MUI spacing units (16px) — used for header, content, and gaps

const InvoiceList = styled(BasicFlex)`
  margin-bottom: 1rem;
`;

const InvoiceCardStyles = styled(Card)`

`;

const ActionsBarStyles = styled(BasicFlex)`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const actionItemStyles = props => `
  display: flex;
  width: 3.5rem;
  height: 3.5rem;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: ${props.theme.palette?.text?.secondary || '#666'};
  border-right: 1px solid ${props.theme.colors.border};
  cursor: pointer;
  transition: background-color .15s, color .15s;

  &:last-child {
    border-right: none;
  }

  && .MuiSvgIcon-root {
    color: inherit;
  }

  &:hover {
    background-color: ${props.theme.palette?.action?.hover || 'rgba(0,0,0,.04)'};
    color: ${props.$hoverColor || props.theme.palette?.text?.primary || '#222'};
  }
`;

const ActionItem = styled(BasicFlex)`
  ${props => actionItemStyles(props)}
`;

const LinkItem = styled(Link)`
  ${props => actionItemStyles(props)}
`;

const Status = styled.div`
  font-size: 1.2rem;
`;

const ItemsBlock = styled.div`
  padding: 1.2rem 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-size: 1.4rem;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.2rem;
  padding: .3rem 0;
`;

const ItemName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemQty = styled.span`
  margin-right: .8rem;
  color: ${props => props.theme.palette?.text?.secondary || '#666'};
`;

const ItemAmount = styled.span`
  flex-shrink: 0;
  color: ${props => props.theme.palette?.text?.secondary || '#666'};
`;

const MoreToggle = styled.button`
  margin-top: .4rem;
  padding: 0;
  border: none;
  background: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: ${props => props.theme.palette?.info?.main || 'inherit'};

  &:hover {
    text-decoration: underline;
  }
`;

const getLineTotal = item => item.line_total ?? (Number(item.qty || 0) * Number(item.unit_price || 0));

const LineItems = ({ items = [] }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items.length) return null;

  const shown = expanded ? items : items.slice(0, MAX_ITEMS_SHOWN);
  const hiddenCount = items.length - MAX_ITEMS_SHOWN;

  return (
    <ItemsBlock>
      {shown.map((item, i) => (
        <ItemRow key={item.line_item_id ?? i}>
          <ItemName title={item.name}>
            <ItemQty>{item.qty}×</ItemQty>{item.name}
          </ItemName>
          <ItemAmount>{formatCurrency(item.calculated_amount)}</ItemAmount>
        </ItemRow>
      ))}
      {hiddenCount > 0 && (
        <MoreToggle type="button" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `+${hiddenCount} more ${hiddenCount === 1 ? 'item' : 'items'}`}
        </MoreToggle>
      )}
    </ItemsBlock>
  );
};

const ActionsBar = ({ invoice }) => {
  const theme = useTheme();
  return (
    <ActionsBarStyles>
      <Tooltip title="View Invoice">
        <LinkItem $hoverColor={theme.palette.info.main} to={`/invoices/view/${invoice.invoice_id}`}>
          <SearchRoundedIcon fontSize="small" />
        </LinkItem>
      </Tooltip>
      <ActionItem $hoverColor={theme.palette.success.main}>
        <DownloadButton noColor url={`/invoices/generate/${invoice.invoice_id}`} title="Print Invoice" />
      </ActionItem>
      <Tooltip title="Email Invoice">
        <ActionItem $hoverColor={theme.palette.primary.main}>
          <AttachEmailRoundedIcon fontSize="small" />
        </ActionItem>
      </Tooltip>
      <Tooltip title="Edit Invoice">
        <LinkItem $hoverColor={theme.palette.secondary.main} to={`/invoices/edit/${invoice.invoice_id}`}>
          <EditRounded fontSize="small" />
        </LinkItem>
      </Tooltip>
      <Tooltip title="Delete Invoice">
        <DeleteDialog url="invoices" id={invoice.invoice_id} toggle={<ActionItem $hoverColor={theme.palette.error.main}>
          <DeleteRounded fontSize="small" />
        </ActionItem>}
          warning_msg={<WarningMessage />}
        />
      </Tooltip>
    </ActionsBarStyles>
  )
}

const InvoiceCard = ({invoice}) => {
  const { isMobile } = useIsMobile();
  return (
    <InvoiceCardStyles variant="outlined" sx={{width: "100%"}}>
      <CardHeader 
      title={<BasicFlex justify="flex-start" gap={isMobile ? "2rem" : "3.5rem"} align="flex-start">
        <div>Invoice #{invoice.invoice_num}</div>
        <Status>
        <MultiStatusChip fs="1.2rem" py=".2rem" value={invoice.paid_status} values={[{value: "unpaid", label: "Unpaid", status: "error"}, {value: "partially_paid", label: "Partially Paid", status: "warning"}, {value: "paid", label: "Paid", status: "success"}]} /></Status>
        </BasicFlex>}
      subheader={`${formatGenericDate(invoice.date)}`} 
      action={<ActionsBar invoice={invoice} />}
      sx={{ p: CARD_SPACING, pb: 0 }}
      slotProps={{title: {sx: {fontSize: isMobile ? "1.4rem" : "1.6rem"}}, subheader: {sx: {fontSize: isMobile ? "1.2rem" : "1.2rem"}}}}
      />
      <CardContent
        sx={{
          p: CARD_SPACING,
          '&:last-child': { pb: CARD_SPACING },
          display: 'flex',
          flexDirection: 'column',
          gap: CARD_SPACING,
        }}
      >
        <LineItems items={invoice.line_items} />
        <BasicFlex justify="flex-start" gap={isMobile ? "2rem" : "5rem"} align="flex-start">
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Subtotal</LabelUpper>
            <div>{formatCurrency(invoice.subtotal)}</div>
          </BasicFlex>
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Total</LabelUpper>
            <div>{formatCurrency(invoice.total)}</div>
          </BasicFlex>
          <BasicFlex dir="column" width="auto" gap=".2rem">
            <LabelUpper $size="1.2rem">Balance</LabelUpper>
            <div>{formatCurrency(invoice.balance)}</div>
          </BasicFlex>
        </BasicFlex>
      </CardContent>
    </InvoiceCardStyles>
  )
}

const Invoices = () => {

  const { data } = usePageData() || {};

  const { data: invoices, pagination, setPagination } = useSimpleTable({url: `/invoices/customer/${data.customer_id}`});

  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 8}}>
        <Card elevation={0} sx={{backgroundColor: "transparent"}}>
          <CardHeader title="Invoices" sx={{p: 1}} />

          <CardContent sx={{p: 1}}>
            <InvoiceList dir="column" gap="1.5rem">
              {invoices?.rows?.map((invoice) => (
                <InvoiceCard key={invoice.invoice_id} invoice={invoice} />
              ))}
            </InvoiceList>
            <SimplePagination 
              rowsPerPage={pagination?.rowsPerPage} 
              currentPage={pagination?.page} 
              setPagination={setPagination} 
              resultCount={invoices?.count} 
              results={invoices?.rows} 
            />
          </CardContent>

        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <Card variant='outlined'>
          <CardHeader title="Balance History" sx={{p: 1}} />
          
        </Card>
      </Grid>

    </Grid>
  )
}

export default Invoices;